import { dataLayer } from "../../../../helpers/dataLayer";
import { env } from "../../../../helpers/env.mjs";
import { CosmosIconChevronDown, CosmosIconLabel } from "@cosmos/web-react";
import classNames from "classnames/bind";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useId, useRef } from "react";
import styles from "./QaStageDropdown.module.css";

const cx = classNames.bind(styles);

/*
 * For use with `event.currentTarget`. This event-property is always the element
 * which the event is bound to, and that is an `HTMLElement` in almost all
 * practical cases. For type-correctness though, we do unfortunately need to
 * perform that type-narrowing on every event.
 */
function assertIsHTMLElement(item: unknown): asserts item is HTMLElement {
  if (item instanceof HTMLElement) {
    return;
  }
  throw new Error("item is not instance of HTMLElement");
}

function onKeyDown({
  event,
  setIsExpanded,
  selectedIndex,
  maxIndex,
  isExpanded,
  setSelectedIndex,
}: {
  event: React.KeyboardEvent;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIndex: number;
  maxIndex: number;
  isExpanded: boolean;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
}): void {
  assertIsHTMLElement(event.currentTarget);

  switch (event.key) {
    case "Escape": {
      setIsExpanded(false);
      break;
    }

    case "Enter":
    case " " /* Space Bar */: {
      // Don't scroll on space-bar press
      event.preventDefault();

      if (!isExpanded) {
        setIsExpanded(true);
      } else {
        const selectedOption = event.currentTarget.querySelector(
          '[aria-selected="true"]',
        );
        if (selectedOption instanceof HTMLElement) {
          selectedOption.click();
        }
      }
      break;
    }

    case "ArrowUp": {
      // Don't scroll
      event.preventDefault();

      if (!isExpanded) {
        setIsExpanded(true);
      } else {
        setSelectedIndex(Math.max(0, selectedIndex - 1));
      }
      break;
    }

    case "ArrowDown": {
      // Don't scroll
      event.preventDefault();

      if (!isExpanded) {
        setIsExpanded(true);
      } else {
        setSelectedIndex(Math.min(selectedIndex + 1, maxIndex));
      }
      break;
    }
  }
}

export const QaStageDropdown = ({
  qaCategoryText,
  activeCategory,
  categories,
  isExpanded,
  setIsExpanded,
  selectedIndex,
  setSelectedIndex,
  setActiveAnswersState,
}: {
  qaCategoryText: string | null;
  activeCategory:
    | {
        title: string | null;
      }
    | undefined;
  categories: { id: string; title: string | null; linkUrl: string | null }[];
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  setActiveAnswersState: React.Dispatch<
    React.SetStateAction<{
      keywords: string;
      categoryId: string | null;
      showAnswers: boolean;
    }>
  >;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const dropdownOptionsElementId = useId();
  const getDropdownOptionId = (index: number) =>
    `${dropdownOptionsElementId}--${index}`;

  const listboxRef = useRef<HTMLDivElement | null>(null);

  return (
    <div className={cx("dropdown")}>
      <div
        className={cx("dropdown-list", {
          "dropdown-list--expanded": isExpanded,
        })}
        ref={listboxRef}
        aria-expanded={isExpanded ? "true" : "false"}
        tabIndex={0}
        role="listbox"
        aria-owns={dropdownOptionsElementId}
        aria-activedescendant={getDropdownOptionId(selectedIndex)}
        onFocus={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        onBlur={(event) => {
          assertIsHTMLElement(event.currentTarget);

          /*
           * `event.relatedTarget` is (for a blur event) the element which is
           * to receive the new focus. Note that for security reasons this
           * may be null, like when tabbing out of a page.
           *
           * For more details see:
           * https://developer.mozilla.org/en-US/docs/Web/API/FocusEvent/relatedTarget
           */
          const elementReceivingFocus = event.relatedTarget;
          const listboxElement = event.currentTarget;

          const hasFocusMovedInsideListbox =
            elementReceivingFocus instanceof HTMLElement &&
            listboxElement.contains(elementReceivingFocus);

          if (!hasFocusMovedInsideListbox) {
            listboxElement.scrollTop = 0;
            setIsExpanded(false);
          }
        }}
        onKeyDown={(event) =>
          onKeyDown({
            event,
            setIsExpanded,
            selectedIndex,
            maxIndex: categories.length - 1,
            isExpanded,
            setSelectedIndex,
          })
        }
      >
        <div
          className={cx("dropdown-label", {
            "dropdown-label--active-category": activeCategory,
          })}
          onMouseDown={() => {
            setIsExpanded((current) => !current);
          }}
        >
          <CosmosIconLabel
            className={cx("dropdown-label-tag-icon")}
            aria-hidden="true"
          />
          {activeCategory ? activeCategory.title : qaCategoryText}
          <CosmosIconChevronDown
            className={cx("dropdown-label-chevron-icon")}
            aria-hidden="true"
          />
        </div>
        <div className={cx("dropdown-options")} id={dropdownOptionsElementId}>
          {categories &&
            categories.map((category, index) => {
              const params = new URLSearchParams(searchParams?.toString());
              params.set("category", category.linkUrl ?? "");
              const href = `${pathname}?${params.toString()}`;

              return (
                <a
                  key={index}
                  id={getDropdownOptionId(index)}
                  className={cx("dropdown-option")}
                  href={href}
                  onClick={(event) => {
                    assertIsHTMLElement(event.currentTarget);
                    event.preventDefault();
                    const listboxElement = listboxRef.current;
                    if (listboxElement) {
                      listboxElement.scrollTop = 0;
                    }
                    setIsExpanded(false);

                    setSelectedIndex(index);

                    /**
                     * Update the query-string without actually reloading the
                     * page.
                     */
                    const newSearchParams = new URLSearchParams(
                      searchParams?.toString(),
                    );
                    if (category.linkUrl) {
                      newSearchParams.set("category", category.linkUrl);
                    }

                    router.replace(`${pathname}?${newSearchParams.toString()}`);

                    setActiveAnswersState((current) => ({
                      ...current,
                      categoryId: category.id,
                      showAnswers: true,
                    }));
                    if (env.NEXT_PUBLIC_HAS_GTM_CONTAINER) {
                      dataLayer.push({
                        event: "virtPath",
                        virtPath: href,
                      });
                    }
                  }}
                  aria-selected={selectedIndex === index ? "true" : "false"}
                  role="option"
                  tabIndex={-1}
                >
                  {category.title}
                </a>
              );
            })}
        </div>
      </div>
    </div>
  );
};
